import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, ProtectedRoute, useAuth } from "./lib/auth";
import { Toaster } from "sonner";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import AdminLogin from "./pages/AdminLogin";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Layout from "./components/Layout";

import AdminOverview from "./pages/admin/AdminOverview";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminBatches from "./pages/admin/AdminBatches";
import AdminFees from "./pages/admin/AdminFees";
import AdminNotifications from "./pages/admin/AdminNotifications";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminStudentDetail from "./pages/admin/AdminStudentDetail";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import AdminAuditLog from "./pages/admin/AdminAuditLog";

import TeacherOverview from "./pages/teacher/TeacherOverview";
import TeacherAttendance from "./pages/teacher/TeacherAttendance";
import TeacherHomework from "./pages/teacher/TeacherHomework";
import TeacherTests from "./pages/teacher/TeacherTests";

import StudentOverview from "./pages/student/StudentOverview";
import StudentTests from "./pages/student/StudentTests";
import StudentTestAttempt from "./pages/student/StudentTestAttempt";
import StudentHomework from "./pages/student/StudentHomework";
import StudentLeaderboard from "./pages/student/StudentLeaderboard";
import StudentInsights from "./pages/student/StudentInsights";

import ParentOverview from "./pages/parent/ParentOverview";

import ChatPage from "./pages/Chat";
import Subscribe from "./pages/Subscribe";
import PaymentSuccess from "./pages/PaymentSuccess";

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="text-blue-600 font-heading animate-pulse">Loading…</div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={`/dashboard/${user.role}`} replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/dashboard" element={<RoleRedirect />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />

            {/* Admin */}
            <Route
              path="/dashboard/admin"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminOverview />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="students/:id" element={<AdminStudentDetail />} />
              <Route path="batches" element={<AdminBatches />} />
              <Route path="fees" element={<AdminFees />} />
              <Route path="notifications" element={<AdminNotifications />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="chat" element={<ChatPage />} />
              <Route path="subscribe" element={<Subscribe />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="audit" element={<AdminAuditLog />} />
            </Route>

            {/* Teacher */}
            <Route
              path="/dashboard/teacher"
              element={
                <ProtectedRoute roles={["teacher"]}>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<TeacherOverview />} />
              <Route path="attendance" element={<TeacherAttendance />} />
              <Route path="homework" element={<TeacherHomework />} />
              <Route path="tests" element={<TeacherTests />} />
              <Route path="chat" element={<ChatPage />} />
            </Route>

            {/* Student */}
            <Route
              path="/dashboard/student"
              element={
                <ProtectedRoute roles={["student"]}>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<StudentOverview />} />
              <Route path="tests" element={<StudentTests />} />
              <Route path="tests/:id" element={<StudentTestAttempt />} />
              <Route path="homework" element={<StudentHomework />} />
              <Route path="leaderboard" element={<StudentLeaderboard />} />
              <Route path="insights" element={<StudentInsights />} />
              <Route path="chat" element={<ChatPage />} />
              <Route path="subscribe" element={<Subscribe />} />
            </Route>

            {/* Parent */}
            <Route
              path="/dashboard/parent"
              element={
                <ProtectedRoute roles={["parent"]}>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<ParentOverview />} />
              <Route path="insights" element={<StudentInsights />} />
              <Route path="chat" element={<ChatPage />} />
              <Route path="subscribe" element={<Subscribe />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
